import type { Permission } from 'api/types';

type UrlInfo = {
  title: string;
  url: () => string;
  oldUrl?: string;
  // A list grants access when the user has ANY of the listed permissions.
  permission?: Permission | Permission[];
  companyPermission?: Permission;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameterUrl?: (...args: any[]) => string;
};

type UrlKey =
  | 'home'
  | 'reporting'
  | 'monthlyReport'
  | 'performance'
  | 'managers'
  | 'activity'
  | 'coverage'
  | 'allUsers'
  | 'userInformation'
  | 'orders'
  | 'orderInformation'
  | 'orderCreate'
  | 'ordersUpload'
  | 'development'
  | 'tasks'
  | 'keys'
  | 'generateKeys'
  | 'platformKeys'
  | 'platforms'
  | 'logs'
  | 'webhooks'
  | 'createWebhook'
  | 'editWebhook'
  | 'customization'
  | 'customSettings'
  | 'templates'
  | 'templateCreate'
  | 'templateEdit'
  | 'fieldsCustomization'
  | 'emulator'
  | 'workflows'
  | 'supportTickets'
  | 'account'
  | 'branding'
  | 'docs'
  | 'settings'
  | 'company'
  | 'team'
  | 'addMember'
  | 'editMember'
  | 'profile'
  | 'notifications'
  | 'twilioIntegration'
  | 'verification'
  | 'verificationStatus'
  | 'billing'
  | 'creditLedger'
  | 'billingUsage'
  | 'subscriptions'
  | 'invoices'
  | 'subscriptionFlow'
  | 'subscriptionChangePlan'
  | 'onboarding';

export const urls: Record<UrlKey, UrlInfo> = {
  home: {
    title: 'Home',
    url: () => '/app/home',
  },

  reporting: {
    title: 'Reporting',
    url: () => '/app/home/reporting',
  },

  onboarding: {
    title: 'Get started',
    url: () => '/app/onboarding',
  },

  performance: {
    title: 'Monthly performance',
    url: () => '/app/home/performance',
    permission: 'can_view_performance_reports',
  },

  managers: {
    title: 'Team performance',
    url: () => '/app/home/managers',
    permission: ['can_view_orders', 'can_view_team_performance'],
  },

  emulator: {
    title: 'Emulator',
    url: () => '/app/emulator',
    permission: 'can_view_development',
  },

  /**
   * Activity menu
   */
  activity: {
    title: 'Activity',
    url: () => '/app/usage',
  },

  allUsers: {
    title: 'Users',
    url: () => '/app/usage/all-users',
    oldUrl: '/app/users',
    permission: 'can_view_profiles',
  },

  userInformation: {
    title: 'User information',
    url: () => '/app/usage/all-users/:id',
    permission: 'can_view_profiles',
  },

  orders: {
    title: 'Orders',
    url: () => '/app/usage/orders',
    oldUrl: '/app/orders',
    permission: 'can_view_orders',
  },

  orderInformation: {
    title: 'Order information',
    permission: 'can_view_orders',
    url: () => '/app/usage/orders/view/:orderId/',
  },

  orderCreate: {
    title: 'Create order',
    permission: 'can_view_orders',
    url: () => '/app/usage/orders/create/',
  },

  ordersUpload: {
    title: 'Upload orders',
    permission: 'can_view_orders',
    url: () => '/app/usage/orders/upload/',
  },

  tasks: {
    title: 'Tasks',
    parameterUrl: (taskId = ':taskId?') => `/app/usage/tasks/${taskId}/`,
    url: () => '/app/usage/tasks',
    oldUrl: '/app/development/tasks',
    permission: 'can_view_tasks',
  },

  coverage: {
    title: 'Coverage',
    url: () => '/app/usage/coverage',
    oldUrl: '/app/coverage',
    permission: 'can_view_analytics',
  },

  /**
   * Development menu
   */
  development: {
    title: 'Development',
    url: () => '/app/development',
    permission: 'can_view_development',
  },

  workflows: {
    title: 'UX guides',
    url: () => 'https://docs.truv.com/',
    oldUrl: '/app/development/workflows',
    permission: 'can_view_development',
  },

  keys: {
    title: 'API keys',
    url: () => '/app/development/keys',
    permission: 'can_view_keys',
  },

  generateKeys: {
    url: () => '/app/development/keys/generate',
    title: 'Generate new key',
    permission: 'can_view_keys',
  },

  platformKeys: {
    url: () => '/app/development/platforms/:id',
    title: 'Platform keys form',
    permission: 'can_view_platform_keys',
  },

  platforms: {
    url: () => '/app/development/platforms',
    title: 'Platform keys',
    permission: 'can_view_platform_keys',
  },

  logs: {
    title: 'Logs',
    url: () => '/app/development/logs',
    permission: 'can_view_logs',
  },

  webhooks: {
    title: 'Webhooks',
    url: () => '/app/development/webhooks',
    permission: 'can_view_webhooks',
  },

  createWebhook: {
    title: 'Create webhook',
    url: () => '/app/development/webhooks/create',
    permission: 'can_view_webhooks',
  },

  editWebhook: {
    title: 'Edit webhook',
    url: () => '/app/development/webhooks/:id',
    permission: 'can_view_webhooks',
  },

  /**
   * Customization menu
   */
  customization: {
    title: 'Customization',
    url: () => '/app/customization',
    permission: 'can_view_customization',
  },

  account: {
    title: 'Account',
    url: () => '/app/customization/account',
    permission: 'can_view_customization',
  },

  branding: {
    title: 'Branding',
    url: () => '/app/customization/account/branding',
    permission: 'can_view_customization',
  },

  customSettings: {
    title: 'Custom settings',
    url: () => '/app/customization/account/custom',
    oldUrl: '/app/settings/custom',
    permission: 'can_edit_customization',
  },

  twilioIntegration: {
    title: 'Twilio integration',
    url: () => '/app/customization/account/twilio',
    permission: 'can_edit_company_settings',
  },

  templateCreate: {
    title: 'Create template',
    url: () => '/app/customization/templates/create',
    permission: 'can_edit_customization',
  },

  templateEdit: {
    title: 'Edit template',
    url: () => '/app/customization/templates/:id',
    permission: 'can_edit_customization',
  },

  templates: {
    title: 'Templates',
    url: () => '/app/customization/templates',
    permission: 'can_edit_customization',
  },

  fieldsCustomization: {
    title: 'Data',
    url: () => '/app/customization/data',
    oldUrl: '/app/settings/fields',
    permission: 'can_view_customization',
  },

  /**
   * Help menu
   */
  supportTickets: {
    title: 'Support',
    url: () => 'https://support.truv.com/tickets-view',
    permission: 'can_view_support_tickets',
  },

  docs: {
    title: 'Docs',
    url: () => 'https://docs.truv.com/',
    parameterUrl: (isOrdersManager: boolean) =>
      isOrdersManager ? 'https://docs.truv.com/developers/integration/manual-orders/new-user' : urls.docs.url(),
  },

  /**
   * Settings menu
   */
  settings: {
    title: 'Settings',
    url: () => '/app/settings',
  },

  company: {
    title: 'Company',
    url: () => '/app/settings/company',
    permission: 'can_view_company_settings',
  },

  addMember: {
    title: 'Add a team member',
    url: () => '/app/settings/team/add',
    permission: 'can_invite_users',
  },

  editMember: {
    title: 'Edit a team member',
    url: () => '/app/settings/team/member/:id',
    permission: 'can_invite_users',
  },

  team: {
    title: 'Team',
    url: () => '/app/settings/team/:tab?',
    parameterUrl: () => '/app/settings/team',
    permission: 'can_invite_users',
  },

  billing: {
    title: 'Billing',
    url: () => '/app/billing',
    permission: 'can_view_billing',
  },

  subscriptionFlow: {
    title: 'Subscription Setup',
    url: () => '/app/billing/subscription/setup',
    permission: 'can_edit_billing',
  },

  subscriptionChangePlan: {
    title: 'Change plan',
    url: () => '/app/billing/subscription/change-plan',
    permission: 'can_edit_billing',
  },

  profile: {
    title: 'Profile',
    url: () => '/app/settings/profile',
    oldUrl: '/app/profile',
    permission: 'can_view_profile',
  },

  notifications: {
    title: 'Notifications',
    url: () => '/app/settings/notifications',
  },

  /**
   * Billing menu
   */
  subscriptions: {
    title: 'Subscription',
    url: () => '/app/billing/subscription',
    permission: 'can_view_billing_stats',
  },

  invoices: {
    title: 'Invoices',
    url: () => '/app/billing/invoices',
    permission: 'can_view_billing_stats',
  },

  creditLedger: {
    title: 'Credits',
    url: () => '/app/billing/credits',
    permission: 'can_view_billing_stats',
  },

  billingUsage: {
    title: 'Usage',
    url: () => '/app/billing/usage',
    permission: 'can_view_billing',
  },

  monthlyReport: {
    title: 'Monthly report',
    url: () => '/app/billing/monthly-report',
    oldUrl: '/app/home/usage',
    permission: 'can_view_billing',
  },

  /**
   * no auth required
   */
  verificationStatus: {
    title: 'Income and employment verification',
    url: () => '/verification/:id',
  },

  verification: {
    title: 'Employment verification',
    url: () => '/verification/',
  },
} as const;
